import React, { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/supabase';
import {
  fetchSessions,
  revokeOtherSessions,
  revokeSession,
  type SessionPolicy,
  type SessionRow,
} from '@/lib/api/sessions';
import { useToast } from '@/components/shared/ToastProvider';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

// ============================================================================
// Sessions card (D-52, 19A §6.5 / 19H §1.5, §3): the person's own live GoTrue
// sessions — device, IP, when they signed in and last refreshed, the current one
// badged — with a per-row Sign out and "Sign out all other sessions".
//
// Self-service on purpose: every signed-in user sees it, no workspace or role gate,
// and its backend (GET/DELETE/POST /users/me/sessions) acts on the caller's own
// user id only. The same card and the same copy as TPRM7's admin Sessions card
// (its PR #286); the licence backend's own rules apply on top (an aal2 session for
// anything that changes state, and for an account that uses MFA, even to list).
//
// FAIL CLOSED. If the response flags no row as "this device" (the token carried no
// session id) the card offers no sign-out at all and says why: the server would
// refuse (409) rather than risk ending the session making the request, and a button
// that can only fail is worse than none.
//
// WHAT A SIGN-OUT MEANS is stated in the confirm dialog: the session cannot refresh
// any more, but a device may keep working on an access token it already holds until
// that token expires. That bound is the standard's (19A §6.5), not a defect here.
//
// "LAST REFRESHED", NOT "LAST ACTIVE". The time shown is auth.sessions.refreshed_at,
// when the session last renewed its sign-in, which is also what GoTrue's inactivity
// timeout counts from. A session in use renews regularly, but the time can trail the
// last click by up to the access-token lifetime, so the card says what it measures
// and the policy line says "about" (D-52-10).
// ============================================================================

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; sessions: SessionRow[]; policy: SessionPolicy | null };

type Pending = { kind: 'one'; row: SessionRow } | { kind: 'others'; count: number };

export interface SessionsCardProps {
  /** Clock for the relative times; tests pin it. */
  now?: Date;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "just now" / "5 minutes ago" / "3 hours ago" / a date once it is over a day old. */
export function formatSessionTime(iso: string | null, now: Date): string {
  if (!iso) return 'unknown';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'unknown';
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${plural(minutes, 'minute', 'minutes')} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, 'hour', 'hours')} ago`;
  return then.toLocaleDateString();
}

function formatDuration(seconds: number): string {
  if (seconds % 3600 === 0) return plural(seconds / 3600, 'hour', 'hours');
  if (seconds % 60 === 0) return plural(seconds / 60, 'minute', 'minutes');
  return plural(seconds, 'second', 'seconds');
}

/** The standard in words, from the server's own policy (it may have been tuned). */
export function describePolicy(p: SessionPolicy): string {
  const parts: string[] = [];
  if (p.inactivity_timeout_seconds > 0) parts.push(`about ${formatDuration(p.inactivity_timeout_seconds)} without activity`);
  if (p.timebox_seconds > 0) parts.push(`${formatDuration(p.timebox_seconds)} in total`);
  if (parts.length === 2) return `Sessions end after ${parts[0]}, or ${parts[1]}.`;
  if (parts.length === 1) {
    return `Sessions end after ${parts[0]}; there is no time limit on ${
      p.inactivity_timeout_seconds > 0 ? "a session's total length" : 'inactivity'
    }.`;
  }
  return 'No session time limits are configured.';
}

function errorMessage(err: unknown, fallback = 'Request failed'): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

const box: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 24,
  maxWidth: 640,
};
const smallBtn: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  flexShrink: 0,
};

export function SessionsCard({ now }: SessionsCardProps) {
  const { toast } = useToast();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [pending, setPending] = useState<Pending | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchSessions();
      setState({ kind: 'ready', sessions: data.sessions ?? [], policy: data.policy ?? null });
    } catch (err) {
      setState({ kind: 'error', message: errorMessage(err) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revokeOne(row: SessionRow) {
    try {
      await revokeSession(row.id);
      toast({ kind: 'success', title: 'Session signed out.' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Gone already (timed out, or signed out from another screen): the list was
        // stale, not the action wrong.
        toast({ kind: 'info', title: 'That session had already ended.' });
      } else {
        toast({ kind: 'error', title: 'Could not sign out that session.', body: errorMessage(err) });
        return; // nothing changed: leave the list as it is
      }
    }
    await load();
  }

  async function revokeOthers() {
    try {
      const { revoked_count } = await revokeOtherSessions();
      const n = typeof revoked_count === 'number' ? revoked_count : 0;
      toast({ kind: 'success', title: `Signed out ${plural(n, 'other session', 'other sessions')}.` });
    } catch (err) {
      toast({ kind: 'error', title: 'Could not sign out the other sessions.', body: errorMessage(err) });
      return;
    }
    await load();
  }

  const clock = now ?? new Date();
  const ready = state.kind === 'ready' ? state : null;
  const hasCurrent = !!ready?.sessions.some((s) => s.is_current);
  const others = ready ? ready.sessions.filter((s) => !s.is_current) : [];

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Active sessions</h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Where you are signed in right now.
            {ready?.policy ? ` ${describePolicy(ready.policy)}` : ''}
          </p>
        </div>
        {ready && hasCurrent && others.length > 0 && (
          <button type="button" style={smallBtn} onClick={() => setPending({ kind: 'others', count: others.length })}>
            Sign out all other sessions
          </button>
        )}
      </div>

      {state.kind === 'loading' && (
        <div role="status" aria-label="Loading sessions" style={{ display: 'grid', gap: 8 }}>
          {[0, 1].map((i) => (
            <div
              key={i}
              className="animate-pulse"
              style={{ height: 56, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}
            />
          ))}
        </div>
      )}

      {state.kind === 'error' && (
        <div style={{ borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', padding: 16 }}>
          <p style={{ fontSize: 14, margin: 0 }}>Could not load your sessions.</p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>{state.message}</p>
          <button
            type="button"
            style={{ ...smallBtn, marginTop: 12 }}
            onClick={() => {
              setState({ kind: 'loading' });
              void load();
            }}
          >
            Try again
          </button>
        </div>
      )}

      {ready && (
        <>
          {!hasCurrent && ready.sessions.length > 0 && (
            <p
              style={{
                fontSize: 12,
                color: 'var(--text-secondary)',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '8px 12px',
                margin: '0 0 12px',
              }}
            >
              We could not tell which of these is this device, so signing sessions out is unavailable here. Sign in again to
              manage them.
            </p>
          )}
          <ul aria-label="Your active sessions" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
            {ready.sessions.map((s) => (
              <li
                key={s.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{s.label}</span>
                    {s.is_current && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: 'var(--ps-pill-green-bg)',
                          color: 'var(--ps-pill-green-tx)',
                        }}
                      >
                        Current
                      </span>
                    )}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {s.ip ?? 'IP unknown'} · Signed in {s.created_at ? new Date(s.created_at).toLocaleString() : 'unknown'} · Last
                    refreshed {formatSessionTime(s.last_active_at, clock)}
                  </p>
                </div>
                {!s.is_current && hasCurrent && (
                  <button type="button" style={smallBtn} onClick={() => setPending({ kind: 'one', row: s })}>
                    Sign out
                  </button>
                )}
              </li>
            ))}
          </ul>
          {ready.sessions.length === 1 && ready.sessions[0].is_current && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '12px 0 0' }}>This is your only active session.</p>
          )}
          {ready.sessions.length > 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '12px 0 0' }}>
              &ldquo;Last refreshed&rdquo; is when a session last renewed its sign-in. A session that is in use renews regularly,
              but the time can trail the last click.
            </p>
          )}
        </>
      )}

      <ConfirmDialog
        open={pending !== null}
        danger
        title={pending?.kind === 'others' ? 'Sign out all other sessions?' : 'Sign out this session?'}
        body={
          pending?.kind === 'others'
            ? `This signs out the ${plural(pending.count, 'other session', 'other sessions')} listed here. This device stays signed in.`
            : pending?.kind === 'one'
              ? `${pending.row.label} (${pending.row.ip ?? 'IP unknown'}) will be signed out. It cannot refresh again; a device may keep working for a short while on an access token it already holds.`
              : ''
        }
        confirmLabel={pending?.kind === 'others' ? 'Sign out all other sessions' : 'Sign out'}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          const p = pending;
          setPending(null);
          if (p?.kind === 'one') void revokeOne(p.row);
          else if (p?.kind === 'others') void revokeOthers();
        }}
      />
    </div>
  );
}
